/**
 * Price lists and the bill they produce, worked out the way the billing
 * engine does it (`backend/app/platform/billing/engine/rating.py`).
 *
 * Shared by the public pricing page and the admin Prices screen, so a
 * price is previewed with the same arithmetic customers are billed with.
 * Imported as `@lipaflow/shared/pricing`: no API client, no Firebase.
 */

export type PriceBand = { from_qty: number; to_qty: number | null; rate: number | null }

export type PriceList = {
  /** 'student', 'unit' */
  unit: string
  /** 'term', 'month' */
  period: string
  currency: string
  band_pricing: 'graduated' | 'volume'
  bands: PriceBand[]
  min_charge: number
  cadence_discounts: Record<string, number>
}

/** Periods in a year, for the yearly figure: three school terms, twelve months. */
export function periodsPerYear(list: Pick<PriceList, 'period'>): number {
  return list.period === 'term' ? 3 : 12
}

export function yearlyDiscount(list: Pick<PriceList, 'cadence_discounts'>): number {
  return list.cadence_discounts.yearly ?? 0
}

export type EstimateLine = { count: number; rate: number; amount: number; from: number; to: number | null }

export type Estimate = {
  /** One period's bill; null when the count needs an agreed rate. */
  perPeriod: number | null
  /** A year paid up front, with the yearly discount. */
  yearly: number | null
  /** True when the minimum charge set the figure. */
  atMinimum: boolean
  lines: EstimateLine[]
}

/** Half-up to whole shillings, as the engine rounds each line. */
const round = (value: number) => Math.floor(value + 0.5)

/**
 * Each band charges the units inside it (graduated), or the whole count
 * pays its band's rate (volume); each line is rounded; then the minimum.
 * A yearly bill buys every period in the year, less the yearly discount
 * (`subscription_service.convert_trial_to_paid`).
 */
export function estimate(list: PriceList, count: number): Estimate {
  const bands = [...list.bands].sort((a, b) => a.from_qty - b.from_qty)
  if (count <= 0) return { perPeriod: 0, yearly: 0, atMinimum: false, lines: [] }
  const own = bands.find((b) => count >= b.from_qty && (b.to_qty === null || count <= b.to_qty))
  if (!own || own.rate === null) return { perPeriod: null, yearly: null, atMinimum: false, lines: [] }

  const lines: EstimateLine[] = []
  if (list.band_pricing === 'graduated') {
    for (const band of bands) {
      if (band.from_qty > count || band.rate === null) break
      const upto = band.to_qty === null ? count : Math.min(band.to_qty, count)
      const units = upto - band.from_qty + 1
      lines.push({ count: units, rate: band.rate, amount: round(units * band.rate), from: band.from_qty, to: band.to_qty })
    }
  } else {
    lines.push({ count, rate: own.rate, amount: round(count * own.rate), from: own.from_qty, to: own.to_qty })
  }
  const charged = lines.reduce((sum, line) => sum + line.amount, 0)
  const perPeriod = Math.max(charged, list.min_charge)
  const year = perPeriod * periodsPerYear(list)
  const yearly = year - round(year * yearlyDiscount(list))
  return { perPeriod, yearly, atMinimum: charged < list.min_charge, lines }
}

/**
 * A bundle (a school's All-in-one) as the billing engine rates it: each part
 * at its own bands and minimum, as if bought alone, then the bundle's
 * discount off their sum. `null` when any part is priced by agreement.
 */
export function estimateBundle(
  parts: PriceList[],
  discount: number,
  count: number,
  cadence: Pick<PriceList, 'period' | 'cadence_discounts'> = parts[0],
): Estimate {
  if (count <= 0) return { perPeriod: 0, yearly: 0, atMinimum: false, lines: [] }
  const each = parts.map((part) => estimate(part, count))
  if (each.some((e) => e.perPeriod === null)) return { perPeriod: null, yearly: null, atMinimum: false, lines: [] }
  const apart = each.reduce((sum, e) => sum + (e.perPeriod ?? 0), 0)
  const perPeriod = apart - round(apart * discount)
  const year = perPeriod * periodsPerYear(cadence)
  return {
    perPeriod,
    yearly: year - round(year * yearlyDiscount(cadence)),
    atMinimum: each.some((e) => e.atMinimum),
    lines: each.flatMap((e) => e.lines),
  }
}

/**
 * Where each band ends, from where the next starts (bands store only
 * their start, as the API does). The last band is open-ended.
 */
export function withEnds(starts: { from_qty: number; rate: number | null }[]): PriceBand[] {
  const sorted = [...starts].sort((a, b) => a.from_qty - b.from_qty)
  return sorted.map((band, i) => ({ ...band, to_qty: i + 1 < sorted.length ? sorted[i + 1].from_qty - 1 : null }))
}

const n = (value: number) => value.toLocaleString('en-KE')

/** "First 200", "Next 300 (201 – 500)", "Above 1,000"; plain ranges for volume bands. */
export function bandLabel(from: number, to: number | null, graduated: boolean): string {
  if (!graduated) return to === null ? `${n(from)} and above` : `${n(from)} – ${n(to)}`
  if (to === null) return from === 1 ? 'Every one' : `Above ${n(from - 1)}`
  if (from === 1) return `First ${n(to)}`
  return `Next ${n(to - from + 1)} (${n(from)} – ${n(to)})`
}
